import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/BackButton';
import { createComputerGame, getActiveGames } from '../../services/gameApi';
import { sendInvite, getPendingInvites, acceptInvite, declineInvite } from '../../services/gameApi';
import api from '../../services/api';
import './TicTacToeLobby.css';

const TicTacToeLobby = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeGames, setActiveGames] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNameModal, setShowNameModal] = useState(false);
  const [playerName, setPlayerName] = useState('');

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load stored name on mount
  useEffect(() => {
    if (!isAuthenticated) {
      const storedName = localStorage.getItem('ticTacToePlayerName');
      if (storedName) {
        setPlayerName(storedName);
      }
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      // Only load multiplayer data if user is authenticated
      if (isAuthenticated) {
        const [gamesRes, invitesRes, usersRes] = await Promise.all([
          getActiveGames().catch(err => {
            console.error('Error loading games:', err);
            return { data: [] };
          }),
          getPendingInvites().catch(err => {
            console.error('Error loading invites:', err);
            return { data: [] };
          }),
          api.get('/api/chat/profile/visible').catch(err => {
            console.error('Error loading users:', err);
            return { data: [] };
          })
        ]);
        
        setActiveGames(gamesRes.data || []);
        setPendingInvites(invitesRes.data || []);
        const users = usersRes.data || [];
        setAvailableUsers(users.filter(u => u.username !== user?.username));
      } else {
        // For anonymous users, only show computer game option
        setActiveGames([]);
        setPendingInvites([]);
        setAvailableUsers([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateComputerGame = async () => {
    // For anonymous users, show name input modal
    if (!isAuthenticated) {
      // Check if name is already stored
      const storedName = localStorage.getItem('ticTacToePlayerName');
      if (storedName) {
        setPlayerName(storedName);
        setShowNameModal(true);
      } else {
        setShowNameModal(true);
      }
      return;
    }
    
    // For authenticated users, create game directly
    await createGame();
  };

  const createGame = async (displayName = null) => {
    try {
      const response = await createComputerGame(displayName);
      if (response.data && response.data.id) {
        navigate(`/game/tic-tac-toe/${response.data.id}`);
      } else {
        alert('Invalid response from server');
        console.error('Invalid response:', response);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create game';
      alert(errorMsg);
      console.error('Error creating game:', err);
    }
  };

  const handleNameSubmit = () => {
    const trimmedName = playerName.trim();
    if (!trimmedName) {
      alert('Please enter your name');
      return;
    }
    
    // Store name in localStorage
    localStorage.setItem('ticTacToePlayerName', trimmedName);
    setShowNameModal(false);
    createGame(trimmedName);
  };

  const handleSendInvite = async () => {
    if (!selectedUser) {
      alert('Please select a user');
      return;
    }

    try {
      await sendInvite(selectedUser);
      alert('Invite sent!');
      setSelectedUser('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send invite');
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      const response = await acceptInvite(inviteId);
      navigate(`/game/tic-tac-toe/${response.data.id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to accept invite');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await declineInvite(inviteId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to decline invite');
    }
  };

  if (loading) {
    return (
      <div className="tic-tac-toe-lobby">
        <BackButton />
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="tic-tac-toe-lobby">
      <BackButton />
      <div className="lobby-header">
        <h1>⭕ Tic-Tac-Toe Lobby</h1>
        <button onClick={handleCreateComputerGame} className="btn-create-computer">
          Play with Computer
        </button>
      </div>

      {!isAuthenticated && (
        <div className="lobby-info-message">
          <p>🎮 Play against the computer! Login to unlock multiplayer features like inviting friends and playing with other users.</p>
        </div>
      )}

      <div className="lobby-sections">
        {isAuthenticated && (
          <>
            <section className="lobby-section">
              <h2>Pending Invites</h2>
              {pendingInvites.length === 0 ? (
                <p className="empty-state">No pending invites</p>
              ) : (
                <div className="invites-list">
                  {pendingInvites.map(invite => (
                    <div key={invite.id} className="invite-card">
                      <div className="invite-info">
                        <strong>{invite.inviterUsername}</strong> wants to play!
                      </div>
                      <div className="invite-actions">
                        <button onClick={() => handleAcceptInvite(invite.id)} className="btn-accept">
                          Accept
                        </button>
                        <button onClick={() => handleDeclineInvite(invite.id)} className="btn-decline">
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="lobby-section">
              <h2>Invite a Player</h2>
              {availableUsers.length === 0 ? (
                <p className="empty-state">No users available to invite. Make sure other users have visible profiles.</p>
              ) : (
                <div className="invite-form">
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="user-select"
                  >
                    <option value="">Select a user...</option>
                    {availableUsers.map(u => (
                      <option key={u.username} value={u.username}>
                        {u.displayName || u.username}
                      </option>
                    ))}
                  </select>
                  <button onClick={handleSendInvite} className="btn-send-invite" disabled={!selectedUser}>
                    Send Invite
                  </button>
                </div>
              )}
            </section>

            <section className="lobby-section">
              <h2>Active Games</h2>
              {activeGames.length === 0 ? (
                <p className="empty-state">No active games</p>
              ) : (
                <div className="games-list">
                  {activeGames.map(game => {
                    const opponent = game.player1Username === user?.username 
                      ? (game.player2Username || 'Computer')
                      : game.player1Username;
                    
                    return (
                      <div key={game.id} className="game-card" onClick={() => navigate(`/game/tic-tac-toe/${game.id}`)}>
                        <div className="game-card-info">
                          <strong>vs {opponent}</strong>
                          <span className={`game-status-badge ${game.status.toLowerCase()}`}>
                            {game.status}
                          </span>
                        </div>
                        <div className="game-card-action">→</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {showNameModal && (
        <div className="name-modal-overlay" onClick={() => setShowNameModal(false)}>
          <div className="name-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Enter Your Name</h3>
            <p>Choose a name to display during the game</p>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Your name (max 50 characters)"
              maxLength={50}
              className="name-input"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleNameSubmit();
                }
              }}
              autoFocus
            />
            <div className="name-modal-buttons">
              <button onClick={handleNameSubmit} className="btn-submit-name">
                Start Game
              </button>
              <button onClick={() => setShowNameModal(false)} className="btn-cancel-name">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicTacToeLobby;

